import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChefHat, Plus, Pencil, Loader2, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import { UNITS, unitLabel, COMPETENCES } from "@/lib/production-i18n";

const CYCLE_LABELS = { codzienny: "Codzienny", cotygodniowy: "Cotygodniowy", na_zapas: "Na zapas" };
const DAYS = [["Pn", 0], ["Wt", 1], ["Śr", 2], ["Cz", 3], ["Pt", 4], ["So", 5], ["Nd", 6]];

export default function ProductionCatalog() {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [orderProducts, setOrderProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editP, setEditP] = useState(null);   // product being edited (or {} for new)
  const [editC, setEditC] = useState(null);   // category being edited (or {} for new)

  async function load() {
    setLoading(true);
    try {
      const [c, p, op] = await Promise.all([
        api.get("/production/categories"),
        api.get("/production/products"),
        api.get("/products?is_active=true&limit=500").catch(() => ({ data: [] })),
      ]);
      setCats(c.data); setProds(p.data); setOrderProducts(op.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd wczytywania"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const byCat = useMemo(() => {
    const m = {};
    prods.forEach((p) => { (m[p.category_id || "none"] = m[p.category_id || "none"] || []).push(p); });
    return m;
  }, [prods]);

  const catName = (id) => cats.find((c) => c.id === id)?.name_pl || "Bez kategorii";

  return (
    <div className="space-y-5" data-testid="production-catalog-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tt-display flex items-center gap-2">
            <ChefHat className="h-6 w-6 text-[var(--tt-primary)]" />Produkcja — katalog
          </h1>
          <p className="text-[var(--tt-muted)] text-sm mt-1">
            Zarządzanie półproduktami i poziomami docelowymi (PAR). Kuchnia tylko czyta.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditC({})} data-testid="add-category-btn">
            <FolderPlus className="h-4 w-4 mr-2" />Kategoria
          </Button>
          <Button onClick={() => setEditP({})} data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-2" />Produkt
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
      ) : (
        <div className="space-y-4">
          {cats.map((c) => (
            <Card key={c.id} className="tt-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-semibold tt-display">{c.name_pl}
                      {c.name_ru && <span className="text-[var(--tt-muted)] font-normal ml-2 text-sm">· {c.name_ru}</span>}
                    </div>
                    {c.competence && <div className="text-xs text-[var(--tt-muted)]">Kompetencja: {c.competence}</div>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setEditC(c)}><Pencil className="h-3.5 w-3.5" /></Button>
                </div>
                <div className="divide-y divide-[var(--tt-border)]">
                  {(byCat[c.id] || []).map((p) => (
                    <ProductRow key={p.id} p={p} onEdit={() => setEditP(p)} onReload={load} />
                  ))}
                  {(byCat[c.id] || []).length === 0 && (
                    <div className="text-sm text-[var(--tt-muted)] py-3">Brak produktów w tej kategorii.</div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {(byCat["none"] || []).length > 0 && (
            <Card className="tt-card"><CardContent className="p-4">
              <div className="font-semibold tt-display mb-2">Bez kategorii</div>
              <div className="divide-y divide-[var(--tt-border)]">
                {byCat["none"].map((p) => <ProductRow key={p.id} p={p} onEdit={() => setEditP(p)} onReload={load} />)}
              </div>
            </CardContent></Card>
          )}
        </div>
      )}

      {editP && <ProductForm initial={editP} cats={cats} orderProducts={orderProducts}
                             onClose={() => { setEditP(null); load(); }} />}
      {editC && <CategoryForm initial={editC} onClose={() => { setEditC(null); load(); }} />}
    </div>
  );
}

function ProductRow({ p, onEdit, onReload }) {
  const [busy, setBusy] = useState(false);
  async function toggle() {
    setBusy(true);
    try { await api.post(`/production/products/${p.id}/toggle-active`); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
    finally { setBusy(false); }
  }
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`prod-row-${p.id}`}>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${p.is_active ? "" : "line-through text-[var(--tt-muted)]"}`}>{p.name_pl}</div>
        <div className="text-xs text-[var(--tt-muted)]">
          {unitLabel(p.unit, "pl")}
          {p.par_target != null && <> · PAR {Number(p.par_target)}</>}
          {" · "}<span className="tt-badge tt-sev-neutral text-[10px]">{CYCLE_LABELS[p.cycle_type] || p.cycle_type}</span>
        </div>
      </div>
      <Switch checked={p.is_active} onCheckedChange={toggle} disabled={busy} />
      <Button variant="ghost" size="sm" onClick={onEdit}><Pencil className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

function ProductForm({ initial, cats, orderProducts, onClose }) {
  const isEdit = !!initial.id;
  // P2f (2026-07-05) — szefowa kuchni: domyślnie WSZYSTKO w paczkach,
  // TYLKO krewetki liczone na kg. Auto-detect po nazwie (krewetk / shrimp / prawn / กุ้ง / креветк).
  function detectDefaultUnit(name) {
    const s = (name || "").toLowerCase();
    if (/krewetk|shrimp|prawn|กุ้ง|креветк/i.test(s)) return "kg";
    return "paczka";
  }
  const [f, setF] = useState({
    name_pl: initial.name_pl || "", name_en: initial.name_en || "", name_th: initial.name_th || "",
    name_ru: initial.name_ru || "", category_id: initial.category_id || "none",
    unit: initial.unit || (initial.name_pl ? detectDefaultUnit(initial.name_pl) : "paczka"),
    par_target: initial.par_target ?? "",
    cycle_type: initial.cycle_type || "codzienny",
    cycle_days: initial.cycle_days || [],
    order_product_id: initial.order_product_id || "none",
    is_active: initial.is_active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  // P2f — auto-update unit gdy user wpisuje krewetki (tylko dla nowego produktu).
  React.useEffect(() => {
    if (isEdit) return;
    const suggested = detectDefaultUnit(f.name_pl);
    if (f.unit === "paczka" && suggested === "kg") setF((p) => ({ ...p, unit: "kg" }));
  }, [f.name_pl, isEdit]);

  async function save() {
    if (!f.name_pl.trim()) { toast.error("Podaj nazwę (PL)."); return; }
    setBusy(true);
    try {
      const payload = {
        name_pl: f.name_pl.trim(), name_en: f.name_en.trim() || null, name_th: f.name_th.trim() || null,
        name_ru: f.name_ru.trim() || null,
        category_id: f.category_id === "none" ? null : f.category_id,
        unit: f.unit.trim() || "szt",
        par_target: f.par_target === "" ? null : Number(f.par_target),
        cycle_type: f.cycle_type,
        cycle_days: f.cycle_type === "cotygodniowy" ? f.cycle_days : null,
        order_product_id: f.order_product_id === "none" ? null : f.order_product_id,
        is_active: f.is_active,
      };
      if (isEdit) await api.put(`/production/products/${initial.id}`, payload);
      else await api.post("/production/products", payload);
      toast.success(isEdit ? "Zapisano." : "Produkt dodany.");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd zapisu"); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--tt-surface)] rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="production-product-form">
        <h3 className="text-lg font-semibold tt-display mb-3">{isEdit ? "Edytuj produkt" : "Nowy produkt"}</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nazwa PL *</Label><Input value={f.name_pl} onChange={(e) => up("name_pl", e.target.value)} data-testid="prod-name-pl" /></div>
            <div><Label>Nazwa RU</Label><Input value={f.name_ru} onChange={(e) => up("name_ru", e.target.value)} placeholder="для кухни" /></div>
            <div><Label>Nazwa TH</Label><Input value={f.name_th} onChange={(e) => up("name_th", e.target.value)} /></div>
            <div><Label>Nazwa EN</Label><Input value={f.name_en} onChange={(e) => up("name_en", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kategoria</Label>
              <Select value={f.category_id} onValueChange={(v) => up("category_id", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— brak —</SelectItem>
                  {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name_pl}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Jednostka</Label>
              <Select value={f.unit} onValueChange={(v) => up("unit", v)}>
                <SelectTrigger data-testid="prod-unit-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => (
                    <SelectItem key={u} value={u} data-testid={`prod-unit-${u}`}>
                      {unitLabel(u, "pl")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>PAR (poziom docelowy)</Label><Input type="number" min="0" value={f.par_target} onChange={(e) => up("par_target", e.target.value)} className="tt-mono" /></div>
            <div><Label>Cykl</Label>
              <Select value={f.cycle_type} onValueChange={(v) => up("cycle_type", v)}>
                <SelectTrigger data-testid="prod-cycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="codzienny">Codzienny</SelectItem>
                  <SelectItem value="cotygodniowy">Cotygodniowy</SelectItem>
                  <SelectItem value="na_zapas">Na zapas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {f.cycle_type === "cotygodniowy" && (
            <div>
              <Label>Dni cyklu</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {DAYS.map(([lbl, d]) => {
                  const on = (f.cycle_days || []).includes(d);
                  return (
                    <button key={d} type="button"
                      onClick={() => up("cycle_days", on ? f.cycle_days.filter((x) => x !== d) : [...(f.cycle_days || []), d])}
                      className={`h-9 px-3 rounded-md text-sm border ${on ? "bg-[var(--tt-primary)] text-[var(--tt-primary-fg)] border-[var(--tt-primary)]" : "border-[var(--tt-border)] text-[var(--tt-muted)]"}`}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <Label>Powiązany produkt zakupowy (opcjonalnie — sugestia do koszyka)</Label>
            <Select value={f.order_product_id} onValueChange={(v) => up("order_product_id", v)}>
              <SelectTrigger><SelectValue placeholder="— brak —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— brak —</SelectItem>
                {orderProducts.map((op) => <SelectItem key={op.id} value={op.id}>{op.name_pl}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2"><Switch checked={f.is_active} onCheckedChange={(v) => up("is_active", v)} /><Label>Aktywny</Label></div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Anuluj</Button>
          <Button onClick={save} disabled={busy} data-testid="prod-save-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Zapisz
          </Button>
        </div>
      </div>
    </div>
  );
}

function CategoryForm({ initial, onClose }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({
    name_pl: initial.name_pl || "", name_ru: initial.name_ru || "", name_th: initial.name_th || "",
    competence: initial.competence || "", sort_order: initial.sort_order ?? 0, is_active: initial.is_active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  async function save() {
    if (!f.name_pl.trim()) { toast.error("Podaj nazwę (PL)."); return; }
    setBusy(true);
    try {
      const payload = { ...f, name_ru: f.name_ru.trim() || null, name_th: f.name_th.trim() || null,
        competence: f.competence.trim() || null, sort_order: Number(f.sort_order) || 0 };
      if (isEdit) await api.put(`/production/categories/${initial.id}`, payload);
      else await api.post("/production/categories", payload);
      toast.success(isEdit ? "Zapisano." : "Kategoria dodana.");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
    finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--tt-surface)] rounded-xl max-w-md w-full p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold tt-display mb-3">{isEdit ? "Edytuj kategorię" : "Nowa kategoria"}</h3>
        <div className="space-y-3">
          <div><Label>Nazwa PL *</Label><Input value={f.name_pl} onChange={(e) => up("name_pl", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nazwa RU</Label><Input value={f.name_ru} onChange={(e) => up("name_ru", e.target.value)} /></div>
            <div><Label>Nazwa TH</Label><Input value={f.name_th} onChange={(e) => up("name_th", e.target.value)} /></div>
          </div>
          <div>
            <Label>Kompetencja (kto zwykle to robi — informacyjnie)</Label>
            <Select
              value={COMPETENCES.find((k) => k.pl === f.competence)?.key || (f.competence ? "custom" : "none")}
              onValueChange={(v) => {
                if (v === "none") up("competence", "");
                else if (v === "custom") up("competence", f.competence || "");
                else up("competence", COMPETENCES.find((k) => k.key === v)?.pl || "");
              }}
            >
              <SelectTrigger data-testid="cat-competence-trigger"><SelectValue placeholder="— wybierz —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— brak —</SelectItem>
                {COMPETENCES.map((k) => (
                  <SelectItem key={k.key} value={k.key} data-testid={`cat-competence-${k.key}`}>
                    {k.pl} <span className="text-[var(--tt-muted)]">· {k.ru} · {k.th}</span>
                  </SelectItem>
                ))}
                <SelectItem value="custom">Inna (wpisz ręcznie)</SelectItem>
              </SelectContent>
            </Select>
            {!COMPETENCES.find((k) => k.pl === f.competence) && f.competence !== "" && (
              <Input
                className="mt-2"
                value={f.competence}
                onChange={(e) => up("competence", e.target.value)}
                placeholder="np. Tylko szef kuchni"
                data-testid="cat-competence-custom-input"
              />
            )}
          </div>
          <div className="flex items-center gap-2"><Switch checked={f.is_active} onCheckedChange={(v) => up("is_active", v)} /><Label>Aktywna</Label></div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Anuluj</Button>
          <Button onClick={save} disabled={busy}>Zapisz</Button>
        </div>
      </div>
    </div>
  );
}
